import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { X, FileText, ArrowUpRight, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DELAY_MS = 30_000;
const SCROLL_THRESHOLD = 0.5; // 50%
const STORAGE_DISMISSED = "ssg_slidein_dismissed_v1";
const STORAGE_CONVERTED = "ssg_slidein_converted_v1";

// Pages that have their own dedicated lead magnet — suppress the slide-in here.
const SUPPRESSED_PATHS = ["/performance", "/hyrox-race-prep-guide"];

export const SlideInLeadCard = () => {
  const location = useLocation();
  const suppressed = SUPPRESSED_PATHS.includes(location.pathname);
  const [visible, setVisible] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [converted, setConverted] = useState(false);

  const hasTouched = useCallback(() => {
    try {
      return (
        localStorage.getItem(STORAGE_DISMISSED) === "1" ||
        localStorage.getItem(STORAGE_CONVERTED) === "1"
      );
    } catch (_e) {
      return false;
    }
  }, []);

  useEffect(() => {
    if (suppressed) {
      setVisible(false);
      return;
    }
    if (hasTouched()) return;

    let opened = false;
    const open = () => {
      if (opened) return;
      opened = true;
      setVisible(true);
    };

    const timer = setTimeout(open, DELAY_MS);

    const onScroll = () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      if (total <= 0) return;
      const pct = window.scrollY / total;
      if (pct >= SCROLL_THRESHOLD) open();
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [hasTouched, suppressed]);

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(STORAGE_DISMISSED, "1");
    } catch (_e) {
      /* localStorage unavailable — ignore */
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        source: "slidein_operating_system",
      });
      setConverted(true);
      try {
        localStorage.setItem(STORAGE_CONVERTED, "1");
      } catch (_e) {
        /* localStorage unavailable — ignore */
      }
      if (res.data?.download_url) {
        window.open(res.data.download_url, "_blank", "noopener,noreferrer");
      }
      toast.success("Your PDF is opening in a new tab.");
      // Auto-hide after a moment so it doesn't linger
      setTimeout(() => setVisible(false), 4500);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="slide-in-lead-card"
          initial={{ opacity: 0, y: 30, x: 30 }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          exit={{ opacity: 0, y: 30, x: 30 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-40 w-[calc(100vw-2rem)] max-w-[360px] bg-[#0A0A0A] text-white border border-white/10 shadow-2xl"
        >
          <button
            type="button"
            onClick={dismiss}
            data-testid="slide-in-close"
            aria-label="Dismiss"
            className="absolute top-3 right-3 p-1.5 text-neutral-400 hover:text-white hover:bg-white/5 transition-colors"
          >
            <X size={16} strokeWidth={1.8} />
          </button>

          <div className="p-6 pb-7">
            {converted ? (
              <div data-testid="slide-in-success">
                <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-bold text-[#E25555] mb-3">
                  <CheckCircle2 size={14} strokeWidth={2} />
                  On its way
                </div>
                <h3 className="font-display text-xl font-black tracking-tight leading-snug">
                  Your PDF is opening.
                </h3>
                <p className="mt-2 text-[13px] text-neutral-400 leading-relaxed">
                  If your browser blocked the popup, check the new tab — or
                  refresh and try again.
                </p>
              </div>
            ) : (
              <form onSubmit={submit}>
                <div className="flex items-center gap-2 mb-3 text-[10px] tracking-[0.22em] uppercase font-bold text-[#E25555]">
                  <FileText size={13} strokeWidth={1.8} />
                  Free Framework · 17 pages
                </div>
                <h3 className="font-display text-xl md:text-[22px] font-black tracking-tight leading-[1.15]">
                  BUILD: The SSG<br />
                  <span className="italic font-light">Operating System</span>.
                </h3>
                <p className="mt-2 text-[12px] text-neutral-400 leading-relaxed">
                  Grab a copy — the framework I use with leaders to close the
                  gap between where they are and what they&apos;re becoming.
                </p>

                <div className="mt-4 space-y-3">
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    required
                    placeholder="Your name"
                    data-testid="slide-in-name"
                    className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#E25555] focus:ring-0 outline-none px-0 py-2 text-sm text-white placeholder:text-neutral-500 transition-colors"
                  />
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    required
                    placeholder="you@company.com"
                    data-testid="slide-in-email"
                    className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#E25555] focus:ring-0 outline-none px-0 py-2 text-sm text-white placeholder:text-neutral-500 transition-colors"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="slide-in-submit"
                  className="group mt-5 w-full inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-[#E25555] hover:text-white disabled:opacity-60 disabled:cursor-not-allowed px-5 py-3 text-[10px] tracking-[0.22em] uppercase font-bold transition-colors"
                >
                  {loading ? "Sending…" : "Send Me The PDF"}
                  <ArrowUpRight
                    size={14}
                    strokeWidth={1.8}
                    className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                  />
                </button>

                <button
                  type="button"
                  onClick={dismiss}
                  data-testid="slide-in-dismiss"
                  className="mt-3 w-full text-center text-[10px] tracking-[0.22em] uppercase font-semibold text-neutral-500 hover:text-neutral-300 transition-colors"
                >
                  No thanks
                </button>
              </form>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SlideInLeadCard;
